/**
 * VisualizadorDeDocumento — lê um documento da Base de Conhecimento sem copiar
 * (spec 2026-09-27-kb-visualizar-sem-copia §4, §7, §10).
 *
 * Cada página chega do servidor como IMAGEM com a marca d'água de quem vê: não há
 * texto para selecionar. O bloqueio de seleção, Ctrl+C e menu de contexto aqui é
 * reforço de atrito, não controle — captura de tela não é impedível, e a marca é o
 * que identifica quem vazou (spec §4).
 *
 * A imagem vem pelo cliente com o Bearer, como blob, e é exibida por `createObjectURL`
 * revogada a cada troca: não fica no DOM uma URL que abra a página fora da sessão.
 */
import { useEffect, useState, type KeyboardEvent, type SyntheticEvent } from 'react'
import { ActionIcon, Box, Button, Group, Loader, Modal, NumberInput, Stack, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useQuery } from '@tanstack/react-query'
import { IconChevronLeft, IconChevronRight, IconDownload, IconFileCertificate, IconLock } from '@tabler/icons-react'

import { baixarKbEvidencia, baixarKbInteiro, getKbPagina, getKbVisualizacao } from '@/api/kb'
import { FONTE_DADO } from '@/pages/chat/components/Dado'

const MENSAGEM_POR_STATUS: Record<number, string> = {
  403: 'O download do documento inteiro é restrito ao proprietário e à auditoria.',
  404: 'Documento indisponível. Você não tem acesso a ele, ou ele foi removido da Base de Conhecimento.',
  415: 'Este formato ainda não pode ser visualizado.',
  422: 'Não foi possível abrir este documento para visualização.',
  429: 'Muitas páginas em pouco tempo. Aguarde um minuto e tente de novo.',
  // 502 cobre o original fora do S3 E a trilha de pasta sensível que não gravou: a resposta
  // é blob e não traz o `detail` legível, então a frase diz as duas causas.
  502: 'Não foi possível abrir este documento agora: o arquivo original ou o registro de acesso está indisponível. Tente de novo em instantes.',
}

export const RENDERIZADOR_OCUPADO =
  'O visualizador está ocupado com outro documento agora. Tente de novo em alguns segundos.'

/** A resposta de erro de um `responseType: 'blob'` não traz JSON legível: decide o status. */
export function mensagemDoVisualizador(e: unknown): string {
  const resposta = (e as { response?: { status?: number; headers?: Record<string, unknown> } })?.response
  const status = resposta?.status
  // 504 é a fila do PDFium que não andou a tempo (vem com `Retry-After`).
  if (status === 504) return RENDERIZADOR_OCUPADO
  return (status && MENSAGEM_POR_STATUS[status]) || 'Não foi possível carregar o documento.'
}

export const SEM_EVIDENCIA =
  'Documentos com 1 ou 2 páginas não têm evidência: o primeiro e o último terço seriam o documento inteiro.'

/** "1–3 e 8–10", a mesma forma da capa do PDF. */
export function rotuloDasFaixas(faixas: number[][] | null | undefined): string {
  if (!faixas || faixas.length !== 2) return ''
  const f = ([a, b]: number[]) => (a === b ? `${a}` : `${a}–${b}`)
  return `${f(faixas[0])} e ${f(faixas[1])}`
}

export const SEM_PDF_DO_FORMATO =
  'Este formato ainda não sai em PDF, e o download sai sempre em PDF no padrão.'

export function mensagemDoDownload(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status
  const detalhe = (e as { detalhe?: string })?.detalhe
  if (detalhe === 'evidencia_indisponivel') return SEM_EVIDENCIA
  if (status === 422) return 'Não foi possível montar o PDF deste documento.'
  if (status === 429) return 'Muitos downloads em pouco tempo. Tente de novo em um minuto.'
  if (status === 415) return SEM_PDF_DO_FORMATO
  return mensagemDoVisualizador(e)
}

const TECLAS_BLOQUEADAS = new Set(['c', 'x', 's', 'p', 'a'])

function bloquear(e: SyntheticEvent) {
  e.preventDefault()
}

interface Props {
  documentId: number | null
  /** Página em que abre (a citada pelo @Atendente). Limitada ao total. */
  paginaInicial?: number
  onClose: () => void
}

export function VisualizadorDeDocumento({ documentId, paginaInicial = 1, onClose }: Props) {
  const aberto = documentId != null
  // Modal centrado, com o console visível atrás: tela cheia tirava a navegação de vista
  // e deixava só o X (pedido do Carlos, 27/09). No celular a página só cabe em tela cheia.
  const telaPequena = useMediaQuery('(max-width: 768px)') ?? false
  // `null` até a pessoa navegar: a página de abertura sai dos metadados, na mesma
  // renderização, para nenhuma página errada ser pedida (e gravada na trilha) antes.
  // Guardada junto com o documento e a abertura a que se refere: trocar de documento com
  // o modal aberto não pode herdar a página do anterior nem por uma renderização.
  const [navegada, setNavegada] = useState<{ doc: number | null; inicial: number; pagina: number } | null>(null)
  const escolhida =
    navegada && navegada.doc === documentId && navegada.inicial === paginaInicial ? navegada.pagina : null
  const [imagem, setImagem] = useState<string | null>(null)

  const meta = useQuery({
    queryKey: ['kb-visualizacao', documentId],
    queryFn: () => getKbVisualizacao(documentId as number),
    enabled: aberto,
    retry: false,
    staleTime: 60 * 1000,
  })
  const total = meta.data?.paginas ?? 0

  // Ao abrir outro documento (ou reabrir), volta para a página de abertura.
  useEffect(() => {
    setNavegada(null)
  }, [documentId, paginaInicial])

  // A página citada só vale em PDF: em Markdown e DOCX ela é da extração (uma página
  // só), não do PDF diagramado (spec §11, C6). Esses abrem na 1. Citada acima do total
  // (documento trocado depois da resposta) abre na última.
  const abertura = meta.data && meta.data.formato !== 'pdf' ? 1 : Math.max(1, paginaInicial)
  const pagina = Math.min(escolhida ?? abertura, total || Number.MAX_SAFE_INTEGER)

  const podeBuscarPagina = aberto && Boolean(meta.data?.visualizavel) && pagina >= 1 && pagina <= total
  const pag = useQuery({
    queryKey: ['kb-pagina', documentId, pagina],
    queryFn: () => getKbPagina(documentId as number, pagina),
    enabled: podeBuscarPagina,
    retry: false,
    // A imagem leva a marca e a hora de quem vê: não se reaproveita entre aberturas.
    gcTime: 0,
    staleTime: 0,
  })

  useEffect(() => {
    if (!pag.data) {
      setImagem(null)
      return
    }
    const url = URL.createObjectURL(pag.data)
    setImagem(url)
    return () => URL.revokeObjectURL(url)
  }, [pag.data])

  const irPara = (n: number) => {
    if (total > 0) setNavegada({ doc: documentId, inicial: paginaInicial, pagina: Math.min(Math.max(1, n), total) })
  }

  // Enquanto o visualizador está aberto, copiar e os atalhos de cópia/impressão ficam
  // mortos na página inteira, não só com o foco dentro dele (o foco inicial do modal
  // vai para o botão de fechar, fora da área).
  useEffect(() => {
    if (!aberto) return
    const semCopia = (e: Event) => e.preventDefault()
    const semAtalho = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && TECLAS_BLOQUEADAS.has(e.key.toLowerCase())) e.preventDefault()
    }
    document.addEventListener('copy', semCopia)
    document.addEventListener('cut', semCopia)
    document.addEventListener('keydown', semAtalho)
    return () => {
      document.removeEventListener('copy', semCopia)
      document.removeEventListener('cut', semCopia)
      document.removeEventListener('keydown', semAtalho)
    }
  }, [aberto])

  const aoTeclar = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && TECLAS_BLOQUEADAS.has(e.key.toLowerCase())) {
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowRight') irPara(pagina + 1)
    if (e.key === 'ArrowLeft') irPara(pagina - 1)
  }

  const [baixando, setBaixando] = useState<'inteiro' | 'evidencia' | null>(null)
  const baixar = async (modo: 'inteiro' | 'evidencia') => {
    if (documentId == null || baixando) return
    setBaixando(modo)
    try {
      await (modo === 'inteiro' ? baixarKbInteiro(documentId) : baixarKbEvidencia(documentId))
    } catch (e) {
      notifications.show({ color: 'red', title: 'Não foi possível baixar', message: mensagemDoDownload(e) })
    } finally {
      setBaixando(null)
    }
  }
  const evidencia = meta.data?.evidencia
  const faixas = rotuloDasFaixas(evidencia?.faixas)

  const erroMeta = meta.isError ? mensagemDoVisualizador(meta.error) : null
  const semVisualizacao = meta.data && !meta.data.visualizavel
  // Com os metadados já lidos, um 404 da página é página fora do documento (o documento
  // mudou), não falta de acesso.
  const erroPagina = !pag.isError
    ? null
    : (pag.error as { response?: { status?: number } })?.response?.status === 404 && total > 0
      ? 'Esta página não existe mais no documento. Volte para a primeira página.'
      : mensagemDoVisualizador(pag.error)

  return (
    <Modal
      opened={aberto}
      onClose={onClose}
      fullScreen={telaPequena}
      size="min(1100px, 92vw)"
      centered
      title={<Text fw={700}>{meta.data?.titulo ?? 'Documento'}</Text>}
      styles={{ body: { paddingBottom: 0 } }}
    >
      <Stack
        gap="sm"
        data-visualizador
        tabIndex={0}
        onKeyDown={aoTeclar}
        onCopy={bloquear}
        onCut={bloquear}
        onContextMenu={bloquear}
        onDragStart={bloquear}
        style={{ userSelect: 'none', WebkitUserSelect: 'none', outline: 'none' }}
      >
        <Group gap="sm" justify="flex-start" wrap="wrap">
          <Tooltip label="Página anterior">
            <ActionIcon variant="default" aria-label="Página anterior" disabled={pagina <= 1 || !total} onClick={() => irPara(pagina - 1)}>
              <IconChevronLeft size={16} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <Text size="sm" aria-live="polite" style={{ fontFamily: FONTE_DADO }}>
            página {total ? pagina : '–'} de {total || '–'}
          </Text>
          <Tooltip label="Próxima página">
            <ActionIcon variant="default" aria-label="Próxima página" disabled={!total || pagina >= total} onClick={() => irPara(pagina + 1)}>
              <IconChevronRight size={16} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
          <NumberInput
            aria-label="Ir para a página"
            size="xs"
            w={90}
            min={1}
            max={total || 1}
            value={pagina}
            disabled={!total}
            onChange={(v) => typeof v === 'number' && irPara(v)}
            styles={{ input: { fontFamily: FONTE_DADO } }}
          />
          {meta.data?.visualizavel && (
            <Tooltip label={SEM_EVIDENCIA} disabled={Boolean(evidencia?.disponivel)} multiline w={280}>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFileCertificate size={14} stroke={1.5} />}
                disabled={!evidencia?.disponivel}
                loading={baixando === 'evidencia'}
                onClick={() => baixar('evidencia')}
              >
                {evidencia?.disponivel ? `Baixar evidência (páginas ${faixas})` : 'Baixar evidência'}
              </Button>
            </Tooltip>
          )}
          {meta.data?.pode_baixar_inteiro ? (
            <Tooltip label={SEM_PDF_DO_FORMATO} disabled={meta.data.visualizavel} multiline w={280}>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconDownload size={14} stroke={1.5} />}
                disabled={!meta.data.visualizavel}
                loading={baixando === 'inteiro'}
                onClick={() => baixar('inteiro')}
              >
                Baixar inteiro
              </Button>
            </Tooltip>
          ) : (
            meta.data && (
              <Group gap={4} wrap="nowrap">
                <IconLock size={13} stroke={1.5} color="var(--mantine-color-gray-6)" aria-hidden />
                <Text size="xs" c="dimmed">
                  O download do documento inteiro é restrito ao proprietário e à auditoria.
                </Text>
              </Group>
            )
          )}
        </Group>

        <Box
          style={{
            minHeight: 360,
            // A página rola dentro do modal; o modal não cresce além da janela.
            maxHeight: telaPequena ? undefined : 'calc(100vh - 260px)',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            overflow: 'auto',
            background: 'var(--ai-body-bg)',
            padding: 12,
            borderRadius: 8,
          }}
        >
          {(meta.isLoading || pag.isLoading) && <Loader color="inovareTeal" size="sm" />}
          {erroMeta && <Text size="sm" role="alert">{erroMeta}</Text>}
          {semVisualizacao && <Text size="sm" role="alert">{MENSAGEM_POR_STATUS[415]}</Text>}
          {erroPagina && <Text size="sm" role="alert">{erroPagina}</Text>}
          {imagem && !pag.isLoading && (
            <img
              src={imagem}
              alt={`Página ${pagina} de ${total} de ${meta.data?.titulo ?? 'documento'}`}
              draggable={false}
              style={{ maxWidth: '100%', height: 'auto', boxShadow: 'var(--ai-shadow-sm)', pointerEvents: 'none' }}
            />
          )}
        </Box>

        <Text size="xs" c="dimmed" pb="sm">
          Cópia desativada. Cada página leva a sua identificação.
        </Text>
      </Stack>
    </Modal>
  )
}
